/**
 * Model-readiness report UI (spec §2 item 1): one card per capability with
 * found/partial/missing status and confidence, plus the generation gate
 * banner ("Fire alarm generation blocked: no exits marked — Mark exits").
 *
 * Shapes match `istari_core::ReadinessReport` (capabilities + generation
 * gates). Pure component: data in, anchors out. The page supplies the wizard
 * href.
 */
import type { CSSProperties } from "react";
import type { ReadinessCapability, ReadinessReport, ReadinessStatus } from "../api/types";

const CAPABILITY_LABELS: Record<string, string> = {
  spaces_present: "Spaces",
  space_footprints: "Space footprints",
  occupancy_classified: "Occupancy classification",
  exits_marked: "Exits",
  ceiling_grids: "Ceiling grids",
  raceways: "Raceways",
};

const FEATURE_LABELS: Record<string, string> = {
  fire_alarm: "Fire alarm",
  hvac: "HVAC",
  layout: "Layout",
};

/** Wizard call-to-action per capability key. */
const CAPABILITY_ACTIONS: Record<string, string> = {
  exits_marked: "Mark exits",
  occupancy_classified: "Review occupancies",
  spaces_present: "Review spaces",
  space_footprints: "Review spaces",
  structural_grid: "Ingest structural grid (buildinggen --json sidecar)",
};

/**
 * Capabilities the normalization wizard CANNOT fix, so their gate must not render
 * a wizard link. `structural_grid` arrives through the sidecar-ingest route after
 * parse — sending a user to the wizard would be a dead end, and the default
 * "Open normalization wizard" label made the gate claim otherwise.
 */
const NOT_WIZARD_FIXABLE: ReadonlySet<string> = new Set(["structural_grid"]);

/** Human label per model-quality check name (ENG-17381 `blocked_by_quality`). */
const QUALITY_CHECK_LABELS: Record<string, string> = {
  quality_not_evaluated: "model quality has not yet been evaluated",
};

const STATUS_COLORS: Record<ReadinessStatus, string> = {
  found: "#98c379",
  partial: "#e5c07b",
  missing: "#e06c75",
};

export function capabilityLabel(key: string): string {
  return CAPABILITY_LABELS[key] ?? key.replace(/_/g, " ");
}

export function featureLabel(feature: string): string {
  return FEATURE_LABELS[feature] ?? feature.replace(/_/g, " ");
}

export function qualityCheckLabel(key: string): string {
  return QUALITY_CHECK_LABELS[key] ?? key.replace(/_/g, " ");
}

export interface BlockedGate {
  feature: string;
  capability: string;
  /** e.g. "Fire alarm generation blocked: no exits marked". */
  message: string;
  /** Call-to-action label, e.g. "Mark exits". */
  action: string;
  /**
   * `true` when this entry comes from `blocked_by_quality` (a failing
   * model-quality check) rather than a normalization capability — the
   * normalization wizard cannot fix a quality-check failure, so these never
   * render a wizard link.
   */
  quality?: boolean;
}

/**
 * Flatten the report's generation gates: one entry per (disabled kind,
 * blocking capability) PLUS one entry per (disabled kind, failing quality
 * check) from `blocked_by_quality` — a quality-only block (every capability
 * `Found`, but the model's quality evaluation is pending/blocked) would
 * otherwise render nothing at all for a disabled gate (ENG-17381 code review,
 * PR #100 finding). Deterministic order: gate order, then `blocked_by` order,
 * then `blocked_by_quality` order (all fixed server-side).
 */
export function blockedGates(report: ReadinessReport): BlockedGate[] {
  const detailOf = new Map(report.capabilities.map((c) => [c.key, c.detail]));
  const gates: BlockedGate[] = [];
  for (const gate of report.generation) {
    if (gate.enabled) continue;
    for (const key of gate.blocked_by) {
      gates.push({
        feature: gate.kind,
        capability: key,
        message: `${featureLabel(gate.kind)} generation blocked: ${
          detailOf.get(key) || `${capabilityLabel(key)} missing`
        }`,
        action: CAPABILITY_ACTIONS[key] ?? "Open normalization wizard",
      });
    }
    for (const key of gate.blocked_by_quality ?? []) {
      gates.push({
        feature: gate.kind,
        capability: key,
        message: `${featureLabel(gate.kind)} generation blocked: model quality check failed — ${qualityCheckLabel(key)}`,
        // Quality checks fail at the source model (extraction contract
        // version, missing room boundaries, etc.) — the normalization
        // wizard can't fix them, but re-uploading/re-ingesting the model
        // (from its project page) can. See `ReadinessCards`'s render logic:
        // this links to `projectHref` when the caller has one.
        action: "Manage model uploads",
        quality: true,
      });
    }
  }
  return gates;
}

/** Capability keys → generation kinds they currently block. */
function blocksByCapability(report: ReadinessReport): Map<string, string[]> {
  const map = new Map<string, string[]>();
  for (const gate of report.generation) {
    if (gate.enabled) continue;
    for (const key of gate.blocked_by) {
      const list = map.get(key) ?? [];
      list.push(gate.kind);
      map.set(key, list);
    }
  }
  return map;
}

const cardStyle: CSSProperties = {
  background: "#21213a",
  borderRadius: 8,
  padding: "0.85rem",
  display: "flex",
  flexDirection: "column",
  gap: "0.4rem",
};

function StatusPill({ status }: { status: ReadinessStatus }) {
  return (
    <span
      data-testid="readiness-status"
      style={{
        padding: "0.1rem 0.5rem",
        borderRadius: 999,
        fontSize: "0.72rem",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.05em",
        color: "#12121e",
        background: STATUS_COLORS[status],
      }}
    >
      {status}
    </span>
  );
}

function CapabilityCard({ cap, blocks }: { cap: ReadinessCapability; blocks: string[] }) {
  return (
    <article data-testid="readiness-card" data-capability={cap.key} style={cardStyle}>
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "0.5rem",
        }}
      >
        <h3 style={{ margin: 0, fontSize: "0.95rem" }}>{capabilityLabel(cap.key)}</h3>
        <StatusPill status={cap.status} />
      </header>
      <div style={{ color: "#8e8eb8", fontSize: "0.8rem" }}>
        Confidence: {Math.round(cap.confidence * 100)}%
      </div>
      {cap.detail && <div style={{ fontSize: "0.85rem" }}>{cap.detail}</div>}
      {blocks.length > 0 && (
        <div style={{ fontSize: "0.8rem", color: "#e5c07b" }}>
          Blocks: {blocks.map(featureLabel).join(", ")}
        </div>
      )}
    </article>
  );
}

export interface ReadinessCardsProps {
  report: ReadinessReport;
  /** Href of the normalization wizard for this model. */
  normalizeHref: string;
  /**
   * Href of this model's project page, where the model can be re-uploaded /
   * re-ingested — the actual fix for a `blocked_by_quality` gate (the
   * normalization wizard cannot fix a failing quality check; see
   * `NOT_WIZARD_FIXABLE`). When omitted (no project context available), a
   * quality-blocked gate's action renders as static text rather than a
   * link to nowhere.
   */
  projectHref?: string;
}

export function ReadinessCards({ report, normalizeHref, projectHref }: ReadinessCardsProps) {
  const gates = blockedGates(report);
  const blocksMap = blocksByCapability(report);
  return (
    <div data-testid="readiness-report">
      {gates.length > 0 && (
        <div
          data-testid="readiness-gates"
          style={{
            background: "#3a2530",
            border: "1px solid #e06c75",
            borderRadius: 8,
            padding: "0.75rem",
            marginBottom: "0.9rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.45rem",
          }}
        >
          {gates.map((gate) => (
            <div
              key={`${gate.feature}:${gate.quality ? "quality" : "capability"}:${gate.capability}`}
              data-testid={gate.quality ? "readiness-gate-quality" : "readiness-gate"}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              <span>{gate.message}</span>
              {gate.quality ? (
                projectHref ? (
                  <a
                    href={projectHref}
                    style={{
                      color: "#4a90d9",
                      whiteSpace: "nowrap",
                      fontWeight: 600,
                    }}
                  >
                    {gate.action}
                  </a>
                ) : (
                  <span style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{gate.action}</span>
                )
              ) : NOT_WIZARD_FIXABLE.has(gate.capability) ? (
                <span style={{ whiteSpace: "nowrap", fontWeight: 600 }}>{gate.action}</span>
              ) : (
                <a
                  href={normalizeHref}
                  style={{
                    color: "#4a90d9",
                    whiteSpace: "nowrap",
                    fontWeight: 600,
                  }}
                >
                  {gate.action}
                </a>
              )}
            </div>
          ))}
        </div>
      )}
      {gates.length === 0 && (
        <p data-testid="readiness-clear" style={{ color: "#98c379" }}>
          No generation features are blocked.
        </p>
      )}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))",
          gap: "0.75rem",
        }}
      >
        {report.capabilities.map((cap) => (
          <CapabilityCard key={cap.key} cap={cap} blocks={blocksMap.get(cap.key) ?? []} />
        ))}
      </div>
    </div>
  );
}
